import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Giga "Welcome back" sign-in screen — a standalone, polished re-entry page.
// Warm cream canvas, an animated Giga flower mark, SSO re-auth, and a list of
// the workspaces this browser has signed into before. Purely presentational.
// ─────────────────────────────────────────────────────────────────────────────

function SlackMark({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-11543" data-mp="omjf7682p980">
      <path fill="#36C5F0" d="M5.04 15.17a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52Zm1.27 0a2.52 2.52 0 0 1 5.04 0v6.31a2.52 2.52 0 1 1-5.04 0v-6.31Z" data-id="element-11544" data-mp="jp169fu22u00" />
      <path fill="#2EB67D" d="M8.83 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.83Zm0 1.27a2.52 2.52 0 0 1 0 5.04H2.52a2.52 2.52 0 1 1 0-5.04h6.31Z" data-id="element-11545" data-mp="39f4upfc0dxsu" />
      <path fill="#ECB22E" d="M18.96 8.83a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.83Zm-1.27 0a2.52 2.52 0 0 1-5.04 0V2.52a2.52 2.52 0 1 1 5.04 0v6.31Z" data-id="element-11546" data-mp="5z2c2bxf9lph" />
      <path fill="#E01E5A" d="M15.17 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52Zm0-1.27a2.52 2.52 0 0 1 0-5.04h6.31a2.52 2.52 0 1 1 0 5.04h-6.31Z" data-id="element-11547" data-mp="axdkqjbzdq64" />
    </svg>;
}
function TeamsMark({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-11548" data-mp="3jdp4wfvb0i54">
      <path fill="#5059C9" d="M16.8 9h4.6c.33 0 .6.27.6.6v4.36a2.94 2.94 0 0 1-2.94 2.94h-.01A3.05 3.05 0 0 1 16 13.85V9.6c0-.33.27-.6.8-.6Z" data-id="element-11549" data-mp="3jce3gmpbzmh6" />
      <circle fill="#5059C9" cx="19" cy="5.5" r="2.1" data-id="element-11550" data-mp="3adk9z57arc7u" />
      <circle fill="#7B83EB" cx="12.4" cy="4.6" r="2.9" data-id="element-11551" data-mp="3aevc70l3gbwu" />
      <path fill="#7B83EB" d="M15.9 9H8.9c-.5 0-.9.4-.9.9v7.3a4.4 4.4 0 0 0 3.6 4.33 4.4 4.4 0 0 0 5.2-4.33V9.9c0-.5-.4-.9-.9-.9Z" data-id="element-11552" data-mp="3eduh4l6ts8oa" />
      <path fill="#000" opacity=".1" d="M12.8 9v9.06c0 .38-.26.71-.63.79-.06 0-.11.01-.17.01H8.03A4.5 4.5 0 0 1 8 17.2V9.9c0-.5.4-.9.9-.9h3.9Z" data-id="element-11553" data-mp="30fmmwl16blgt" />
      <rect fill="#4B53BC" x="2" y="7" width="10" height="10" rx="1.4" data-id="element-11554" data-mp="1axh3vzdxdvhx" />
      <path fill="#fff" d="M9.3 9.4H4.7v1.26h1.57v4.3h1.46v-4.3H9.3V9.4Z" data-id="element-11555" data-mp="1azk1p5lbksx7" />
    </svg>;
}
function GoogleMark({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-11556" data-mp="12670zhh0kn0k">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09Z" data-id="element-11557" data-mp="x6ubsq2o73ut" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" data-id="element-11558" data-mp="1tvs6y9ec13si" />
      <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" data-id="element-11559" data-mp="1kuu3ptbpx0az" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51Z" data-id="element-11560" data-mp="1fvqzgb3kay42" />
    </svg>;
}
const WORKSPACES = [{
  id: 'giga-main',
  name: 'Giga Main',
  email: 'faith@example.com',
  initial: 'G',
  active: true
}, {
  id: 'faith',
  name: 'Faith Alqahtani',
  email: 'alqahtani.faith@gmail.com',
  initial: 'F',
  active: false
}];
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 24
    }
  }
};
export function SignIn() {
  return <div className="relative min-h-screen w-full overflow-hidden bg-[#FAF8F4] antialiased" data-id="element-11561" data-mp="i312uu7q7jfz">
      {/* Language selector — top right, so the footer stays a single quiet
       terms line. The flower mark above "Welcome back" carries the brand. */}
      <div className="absolute right-6 top-6 z-10 sm:right-10 sm:top-8" data-id="element-11562" data-mp="s0yr3hpl5g8l">
        <div className="relative" data-id="element-11563" data-mp="2ousff07duz9j">
          <select aria-label="Language" defaultValue="en-US" className="appearance-none rounded-lg border border-[#E7E0D6] bg-white/80 py-2 pl-3 pr-9 text-[13px] font-medium text-neutral-600 outline-none transition-colors hover:border-[#D6CCBE] focus:border-[#EA580C]" data-id="element-11564" data-mp="2ovlgbkjnuujr">
            
            <option value="en-US" data-id="element-11565" data-mp="j4yhi00ctftb">🇺🇸 English (US)</option>
            <option value="en-GB" data-id="element-11566" data-mp="x5rdkp6b0iyj">🇬🇧 English (UK)</option>
            <option value="es" data-id="element-11567" data-mp="x356eqnbs5gu">🇪🇸 Español</option>
            <option value="fr" data-id="element-11568" data-mp="s5wl4h6vrlbd">🇫🇷 Français</option>
            <option value="de" data-id="element-11569" data-mp="s4bu31jryki9">🇩🇪 Deutsch</option>
          </select>
          <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-11570" data-mp="4h3n8f6p73e4" />
          
        </div>
      </div>

      {/* Centered content */}
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-5 py-24" data-id="element-11571" data-mp="e75cvbep8tv5">
        <motion.div variants={container} initial="hidden" animate="show" className="flex w-full max-w-md flex-col items-center" data-id="element-11572" data-mp="9e3w0xuxmk8x">
          
          {/* The Giga flower mark itself — no container box and no glow, just
           the brand mark gently breathing on the cream canvas. */}
          <motion.div variants={item} className="mb-6" data-id="element-11573" data-mp="1sdjli6825g2h">
            <motion.img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-14 w-14 object-contain" animate={{
            y: [0, -4, 0],
            rotate: [0, 3, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-11574" data-mp="1x4t8ez2825ao" />
            
          </motion.div>

          <motion.h1 variants={item} className="text-center font-title text-4xl font-bold tracking-tight text-[#2B211A] sm:text-5xl" data-id="element-11575" data-mp="2g34e7gu0jroy">
            
            Welcome back
          </motion.h1>
          <motion.p variants={item} className="mb-9 mt-2.5 text-center text-[15px] text-neutral-500" data-id="element-11576" data-mp="271efdpxsxqh5">
            Sign in to pick up right where you left off.
          </motion.p>

          {/* SSO buttons — Slack is the primary path (bold espresso pill),
           Teams reads as a quiet secondary option. */}
          <motion.div variants={item} className="w-full space-y-3" data-id="element-11577" data-mp="bo276kcbvucg">
            <button type="button" className="group flex w-full items-center justify-center gap-3 rounded-2xl bg-[#241610] py-4 text-[17px] font-bold text-white shadow-[0_16px_36px_-16px_rgba(36,22,16,0.7)] transition-colors hover:bg-black" data-id="element-11578" data-mp="7lnpw4ob52na">
              
              <SlackMark className="h-[22px] w-[22px]" data-id="element-11579" data-mp="3p0m7nxll4lk7" />
              Continue with Slack
            </button>
            {/* Google is a peer of Slack, not a fallback — teams without Slack
             sign in here and use Giga's web chat. */}
            <button type="button" className="group flex w-full items-center justify-center gap-3 rounded-2xl border border-[#EEE8DE] bg-white py-4 text-[17px] font-bold text-[#2B211A] transition-all hover:border-[#D6CCBE] hover:shadow-md" data-id="element-11580" data-mp="3g0pmw72bg6uf">
              <GoogleMark className="h-[22px] w-[22px]" data-id="element-11581" data-mp="199q5jascgmqm" />
              Continue with Google
            </button>
            {/* Teams isn't live. The row keeps its place so the option is
             visible, and on hover it becomes the one useful action available:
             getting told when it ships. */}
            <button type="button" className="group relative flex w-full items-center justify-center gap-3 rounded-2xl border border-[#EEE8DE] bg-white/60 py-4 text-[17px] font-bold text-neutral-400 transition-all hover:border-[#D6CCBE] hover:bg-white hover:text-[#2B211A]" data-id="element-11582" data-mp="1qjamr1nnneru">
              <TeamsMark className="h-[22px] w-[22px] opacity-50 transition-opacity group-hover:opacity-100" data-id="element-11583" data-mp="xwpt1ujpiql4" />
              <span className="group-hover:hidden" data-id="element-11584" data-mp="17skj3aq9fnxx">Continue with Teams</span>
              <span className="hidden group-hover:inline" data-id="element-11585" data-mp="17ungwgxnmld7">
                Join the waitlist
              </span>
              <span className="absolute right-4 rounded-full bg-[#F0EBE2] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-neutral-500 transition-colors group-hover:bg-[#FFEDD5] group-hover:text-[#B4470F]" data-id="element-11586" data-mp="1brtd8gp7pnu0">
                Soon
              </span>
            </button>
          </motion.div>

          {/* Divider */}
          <motion.div variants={item} className="mt-8 mb-4 flex w-full items-center gap-4" data-id="element-11587" data-mp="34cjoqaj1j1kf">
            
            <span className="h-px flex-1 bg-[#E7E0D6]" data-id="element-11588" data-mp="3j1pw4ls17y3e" />
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-11589" data-mp="3j2he2tsphxb7">
              Your workspaces
            </span>
            <span className="h-px flex-1 bg-[#E7E0D6]" data-id="element-11590" data-mp="3s0bd4chuv522" />
          </motion.div>

          {/* Workspaces */}
          <motion.div variants={item} className="w-full space-y-3" data-id="element-11591" data-mp="3n1gimdaizs24">
            {WORKSPACES.map(w => <button key={w.id} type="button" className="group flex w-full items-center gap-3.5 rounded-xl border border-[#E7E0D6] bg-white p-3.5 text-left shadow-sm transition-all hover:border-[#EA580C]/50 hover:shadow-md" data-id="element-11592" data-mp="5t37ra6xqy61">
              
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white ${w.active ? 'bg-[#EA580C]' : 'bg-[#2B211A]'}`} data-id="element-11593" data-mp="27nccxu8846tp">
                
                  {w.active ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11594" data-mp="i6q9sy2pypw2" /> : w.initial}
                </span>
                <span className="flex min-w-0 flex-1 flex-col" data-id="element-11595" data-mp="3vja41tldukvd">
                  <span className="truncate text-[15px] font-semibold text-[#2B211A]" data-id="element-11596" data-mp="3vhpbg9impc05">
                    {w.name}
                  </span>
                  <span className="truncate text-[13px] text-neutral-500" data-id="element-11597" data-mp="3ll2sjq9v4v0r">
                    {w.email}
                  </span>
                </span>
                {w.active && <Check size={18} className="shrink-0 text-[#EA580C]" strokeWidth={3} data-id="element-11598" data-mp="3qkfm0tv8p0cb" />}
                <ArrowRight size={18} className="shrink-0 text-neutral-300 transition-all group-hover:translate-x-0.5 group-hover:text-[#2B211A]" data-id="element-11599" data-mp="1ju938ba2ozfk" />
              
              </button>)}
          </motion.div>

          <motion.button variants={item} type="button" className="mt-5 text-[13px] font-medium text-neutral-500 underline-offset-2 transition-colors hover:text-[#2B211A] hover:underline" data-id="element-11600" data-mp="2nll5omv4uxgo">
            
            Sign out of all workspaces
          </motion.button>

          <motion.p variants={item} className="mt-9 text-sm text-neutral-600" data-id="element-11601" data-mp="2jmcdhmfqr7n8">
            No account?{' '}
            <Link to="/app-screen-mockups/signup" className="font-semibold text-[#2B211A] underline underline-offset-2 hover:text-[#EA580C]" data-id="element-11602" data-mp="37c6p60obqq5w">
              
              Sign up
            </Link>
          </motion.p>
        </motion.div>
      </div>

      {/* Footer — a single quiet terms line pinned to the bottom */}
      <div className="absolute inset-x-0 bottom-6 z-10 px-5" data-id="element-11603" data-mp="329xtuxnzbc0m">
        <p className="mx-auto text-center text-[12px] leading-relaxed text-neutral-400 sm:whitespace-nowrap" data-id="element-11604" data-mp="1lx99ejrxyikb">
          By logging in, you agree to the Giga{' '}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-neutral-600" data-id="element-11605" data-mp="1jd2w3lhftfr3">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/terms" className="underline underline-offset-2 hover:text-neutral-600" data-id="element-11606" data-mp="1edhv75ipixbq">
            Terms of Service
          </Link>
          .
        </p>
      </div>
    </div>;
}